import { FieldProps } from 'formik'
import React from 'react'
import { Columns, Element, Form } from 'react-bulma-components'
import FormField, { FormFieldProps } from './form-field'

interface TimeInputFieldProps extends FormFieldProps, FieldProps {
  /** Label principal du champ (ex: "Durée") */
  label?: string
  /** Label pour le champ heures */
  hoursLabel?: string
  /** Label pour le champ minutes */
  minutesLabel?: string
  /** Valeur maximale pour les heures */
  maxHours?: number
  /** Valeur maximale pour les minutes */
  maxMinutes?: number
  /** Désactivé */
  disabled?: boolean
}

/**
 * Composant pour saisir des heures et minutes séparément
 * Gère automatiquement la conversion timestamp ↔ heures/minutes
 * S'intègre avec Formik comme les autres champs
 */
const TimeInputField: React.FunctionComponent<TimeInputFieldProps> = ({
  field,
  form,
  label,
  hoursLabel = 'Heures',
  minutesLabel = 'Minutes',
  maxHours,
  maxMinutes = 59,
  disabled = false,
  help,
  required,
}) => {
  // Conversion du timestamp en heures et minutes
  const timestampToHoursMinutes = (timestamp: number): { hours: string; minutes: string } => {
    if (!timestamp || timestamp <= 0) return { hours: '', minutes: '' }

    const totalMinutes = Math.floor(timestamp / (60 * 1000))
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    return {
      hours: hours.toString(),
      minutes: minutes.toString().padStart(2, '0'),
    }
  }

  // Conversion des heures et minutes en timestamp
  const hoursMinutesToTimestamp = (hours: string, minutes: string): number => {
    const hoursNum = parseInt(hours || '0', 10)
    const minutesNum = parseInt(minutes || '0', 10)

    if (isNaN(hoursNum) || isNaN(minutesNum) || hoursNum < 0 || minutesNum < 0) {
      return 0
    }

    return (hoursNum * 60 + minutesNum) * 60 * 1000
  }

  // Valeurs actuelles converties depuis le timestamp
  const { hours: currentHours, minutes: currentMinutes } = timestampToHoursMinutes(field.value || 0)

  // Gestion des erreurs
  const hoursError = form.errors[`${field.name}_hours`] as string
  const minutesError = form.errors[`${field.name}_minutes`] as string
  const hasError = hoursError || minutesError
  const error = hasError ? [hoursError, minutesError].filter(Boolean).join(', ') : undefined

  // Mise à jour du timestamp quand les heures ou minutes changent
  const updateTimestamp = (hours: string, minutes: string) => {
    const timestamp = hoursMinutesToTimestamp(hours, minutes)
    form.setFieldValue(field.name, timestamp)
  }

  return (
    <FormField label={label} error={error} help={help} required={required} disabled={disabled}>
      <Columns>
        <Columns.Column size={6}>
          <Element mb={1}>
            <Element renderAs="label" textSize="small" textWeight="semibold">
              {hoursLabel}
            </Element>
          </Element>
          <Form.Input
            type="number"
            min={0}
            max={maxHours}
            step={1}
            placeholder={'0'}
            value={currentHours}
            data-test={`time-input-hours-${field.name}`}
            onChange={e => {
              const newHours = e.target.value
              const newMinutes = currentMinutes
              updateTimestamp(newHours, newMinutes)
            }}
            disabled={disabled}
            className={hoursError ? 'is-danger' : ''}
          />
        </Columns.Column>

        <Columns.Column size={6}>
          <Element mb={1}>
            <Element renderAs="label" textSize="small" textWeight="semibold">
              {minutesLabel}
            </Element>
          </Element>
          <Form.Input
            type="number"
            min={0}
            max={maxMinutes}
            step={1}
            placeholder={'00'}
            value={currentMinutes}
            data-test={`time-input-minutes-${field.name}`}
            onChange={e => {
              const newHours = currentHours
              const newMinutes = e.target.value
              updateTimestamp(newHours, newMinutes)
            }}
            disabled={disabled}
            className={minutesError ? 'is-danger' : ''}
          />
        </Columns.Column>
      </Columns>
    </FormField>
  )
}

export default TimeInputField
